import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, FileText, Send, XCircle, Coins, Mail, Copy, ArrowUpRight,
  Building2, Loader2, CheckCircle2, Plus, Sparkles, Search,
  AlertTriangle, ArrowUp, ArrowDown, X, Clock, TrendingUp,
  PanelRightClose, PanelRightOpen,
} from "lucide-react";
import FicOnboardingBanner from "@/components/FicOnboardingBanner";

/**
 * Commercialista dashboard v3 — hybrid layout:
 *  - Left: dense sortable table with inline quick-actions (activate / open / copy)
 *  - Right: sticky sidebar with selected client details, mini-KPIs, inbound email
 *  - Top: compact KPI strip + attention row (only when there are alerts)
 */
export default function CommercialistaDashboard({ user, credits }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | alerts | pending | dormant | active
  const [sortKey, setSortKey] = useState("priority");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedId, setSelectedId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const firstName = (user?.full_name || "").split(" ")[0] || "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/commercialista/overview");
        setData(data);
        // auto-select first client with alerts, else first client
        const list = data?.clients || [];
        const alert = list.find((c) => c.sdi_rejected > 0 || c.pending > 0);
        setSelectedId((alert || list[0])?.company_id || null);
      } catch {
        toast.error("Errore caricamento overview");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activate = useCallback(async (cid) => {
    setSwitching(cid);
    try {
      await api.post(`/companies/${cid}/activate`);
      toast.success("Cliente attivato");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
      setSwitching(null);
    }
  }, []);

  const copy = useCallback((v) => {
    navigator.clipboard.writeText(v);
    toast.success("Copiato negli appunti");
  }, []);

  const agg = data?.aggregate || {};
  const allClients = useMemo(() => data?.clients || [], [data?.clients]);

  const clientsWithAlerts = useMemo(
    () => allClients.filter((c) => c.sdi_rejected > 0),
    [allClients]
  );

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? allClients.filter((c) =>
          [c.denominazione, c.partita_iva, c.inbound_email]
            .filter(Boolean).some((v) => v.toLowerCase().includes(q))
        )
      : [...allClients];

    if (filter === "alerts") list = list.filter((c) => c.sdi_rejected > 0);
    else if (filter === "pending") list = list.filter((c) => c.pending > 0);
    else if (filter === "active") list = list.filter((c) => c.is_active);
    else if (filter === "dormant") list = list.filter((c) => isDormant(c.last_activity_at));

    const dir = sortDir === "desc" ? -1 : 1;
    const cmp = {
      priority: (a, b) => {
        // red > amber > green, then higher pending, then more recent activity
        const rank = { red: 3, amber: 2, green: 1 };
        const rA = rank[a.health] || 0;
        const rB = rank[b.health] || 0;
        if (rA !== rB) return (rB - rA) * dir;
        if (b.sdi_rejected !== a.sdi_rejected) return (b.sdi_rejected - a.sdi_rejected) * dir;
        return (b.pending - a.pending) * dir;
      },
      name: (a, b) => (a.denominazione || "").localeCompare(b.denominazione || "") * dir * -1,
      invoices: (a, b) => (b.invoices - a.invoices) * dir,
      volume: (a, b) => ((b.amount_eur || 0) - (a.amount_eur || 0)) * dir,
      activity: (a, b) => {
        const ta = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
        const tb = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
        return (tb - ta) * dir;
      },
    }[sortKey];
    list.sort(cmp);
    return list;
  }, [allClients, search, sortKey, sortDir, filter]);

  const selected = useMemo(
    () => filteredSorted.find((c) => c.company_id === selectedId) || allClients.find((c) => c.company_id === selectedId) || null,
    [selectedId, filteredSorted, allClients]
  );

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "desc" ? "asc" : "desc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (loading) {
    return (
      <div className="p-10 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const dormantCount = allClients.filter((c) => isDormant(c.last_activity_at)).length;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-commercialista">
      <FicOnboardingBanner />
      {/* Hero */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-2">
            <span className="w-6 h-px bg-[#1D4ED8]" />
            Studio commercialista
          </div>
          <h1
            className="font-display font-extrabold text-3xl lg:text-4xl tracking-tighter leading-[1] text-[#0A0A0A]"
            data-testid="commercialista-title"
          >
            Ciao <span className="font-serif-accent text-[#1D4ED8]">{firstName}</span>
          </h1>
          <p className="mt-2 text-sm text-[#4A4A4A]">
            {agg.total_clients ?? 0} client{(agg.total_clients ?? 0) !== 1 ? "i" : "e"} &middot;{" "}
            <span className="font-mono tabular-nums">{formatEur(agg.total_amount_eur)}</span> di volume trattato
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/app/account/credits"
            data-testid="dashboard-credits-btn"
            className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full text-sm font-semibold border ${
              credits && credits.credits_available <= 5
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-white text-[#0A0A0A] border-[#E8E4D9]"
            }`}
            title="Wallet unico condiviso da tutti i clienti"
          >
            <Coins className="w-4 h-4 text-[#1D4ED8]" />
            <span className="tabular-nums font-mono">
              {credits === null ? "\u2026" : credits.credits_available}
            </span>
            <span className="text-[10px] uppercase tracking-wider opacity-70 hidden sm:inline">Wallet</span>
          </Link>
          <Link
            to="/app/account/companies"
            data-testid="dashboard-new-client-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuovo cliente
          </Link>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Kpi label="Clienti" value={agg.total_clients ?? 0} icon={Users} accent="blue" onClick={() => setFilter("all")} active={filter === "all"} />
        <Kpi label="Fatture tot." value={agg.total_invoices ?? 0} icon={FileText} accent="ink" />
        <Kpi label="Da rivedere" value={agg.pending ?? 0} icon={Clock} accent={agg.pending > 0 ? "amber" : "muted"} onClick={() => setFilter(filter === "pending" ? "all" : "pending")} active={filter === "pending"} />
        <Kpi label="Inviate SDI" value={agg.sdi_sent ?? 0} icon={Send} accent="emerald" />
        <Kpi label="Scartate" value={agg.sdi_rejected ?? 0} icon={XCircle} accent={agg.sdi_rejected > 0 ? "red" : "muted"} onClick={() => setFilter(filter === "alerts" ? "all" : "alerts")} active={filter === "alerts"} />
      </div>

      {/* Attention row */}
      {clientsWithAlerts.length > 0 && filter !== "alerts" && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="alerts-section">
          <div className="flex items-center gap-2.5 min-w-0">
            <AlertTriangle className="w-4 h-4 text-red-700 shrink-0" />
            <div className="text-sm text-red-900">
              <span className="font-semibold">{clientsWithAlerts.length} client{clientsWithAlerts.length !== 1 ? "i" : "e"}</span> con fatture scartate dallo SDI &middot; da gestire subito
            </div>
          </div>
          <button
            onClick={() => setFilter("alerts")}
            data-testid="filter-alerts-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-700 text-white text-xs font-semibold hover:bg-red-800 transition-colors"
          >
            Filtra scartati <ArrowUpRight className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="w-4 h-4 text-[#737373] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Cerca cliente per nome, P.IVA o email inbound"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="clients-search"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#E8E4D9] bg-white text-sm focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none transition-colors"
          />
        </div>
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
          Tutti · {allClients.length}
        </FilterChip>
        <FilterChip active={filter === "alerts"} onClick={() => setFilter("alerts")} testid="filter-alerts" tone="red" show={clientsWithAlerts.length > 0}>
          Scartati · {clientsWithAlerts.length}
        </FilterChip>
        <FilterChip active={filter === "pending"} onClick={() => setFilter("pending")} testid="filter-pending" tone="amber" show={(agg.pending ?? 0) > 0}>
          Da rivedere · {agg.pending}
        </FilterChip>
        <FilterChip active={filter === "dormant"} onClick={() => setFilter("dormant")} testid="filter-dormant" tone="muted" show={dormantCount > 0}>
          Dormienti · {dormantCount}
        </FilterChip>
        <button
          onClick={() => setSidebarOpen((v) => !v)}
          data-testid="sidebar-toggle"
          className="hidden lg:inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-[#E8E4D9] bg-white text-xs font-semibold text-[#4A4A4A] hover:text-[#0A0A0A]"
          title={sidebarOpen ? "Nascondi dettagli" : "Mostra dettagli"}
        >
          {sidebarOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
        </button>
      </div>

      {/* Hybrid layout: table + sidebar */}
      <div className={`grid gap-4 ${sidebarOpen && selected ? "lg:grid-cols-[minmax(0,1fr)_360px]" : "grid-cols-1"}`}>
        {/* Table */}
        <div className="rounded-xl border border-[#E8E4D9] bg-white overflow-hidden">
          {filteredSorted.length === 0 ? (
            <EmptyState search={search} filter={filter} onReset={() => { setSearch(""); setFilter("all"); }} />
          ) : (
            <>
              {/* Header */}
              <div className="hidden md:grid grid-cols-[36px_1fr_130px_90px_80px_120px_110px_140px] gap-3 px-4 py-2.5 text-[10px] uppercase tracking-widest text-[#737373] font-bold bg-[#F9F7F1] border-b border-[#E8E4D9]">
                <div />
                <SortHeader label="Cliente" k="name" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Priorità" k="priority" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Fatture" k="invoices" cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <div className="text-right">SDI</div>
                <SortHeader label="Volume" k="volume" cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortHeader label="Attività" k="activity" cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <div className="text-right">Azioni</div>
              </div>
              {/* Rows */}
              <div className="divide-y divide-[#F0EDE4]">
                {filteredSorted.map((c) => (
                  <ClientRow
                    key={c.company_id}
                    c={c}
                    selected={c.company_id === selectedId}
                    switching={switching}
                    onSelect={setSelectedId}
                    onActivate={activate}
                    onCopy={copy}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        {sidebarOpen && selected && (
          <ClientSidebar
            c={selected}
            switching={switching}
            onActivate={activate}
            onCopy={copy}
            onClose={() => setSidebarOpen(false)}
          />
        )}
      </div>

      {/* Wallet footer note */}
      <p className="mt-6 text-xs text-[#737373] flex items-center gap-1.5">
        <Coins className="w-3 h-3" />
        Wallet crediti unico &middot; i crediti sono condivisi tra tutti i clienti che gestisci
      </p>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function Kpi({ label, value, icon: Icon, accent, onClick, active }) {
  const iconBg = {
    blue: "bg-[#1D4ED8]/10 text-[#1D4ED8]",
    emerald: "bg-[#059669]/10 text-[#059669]",
    ink: "bg-[#0A0A0A]/10 text-[#0A0A0A]",
    red: "bg-red-100 text-red-600",
    amber: "bg-amber-100 text-amber-700",
    muted: "bg-[#F5F2EA] text-[#737373]",
  }[accent] || "bg-[#F5F2EA] text-[#4A4A4A]";
  const clickable = typeof onClick === "function";
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`text-left rounded-xl border p-3.5 transition-all ${
        active ? "border-[#0A0A0A] bg-[#0A0A0A]/[0.02] shadow-sm" : "border-[#E8E4D9] bg-white"
      } ${clickable ? "hover:border-[#0A0A0A] hover:shadow-sm cursor-pointer" : ""}`}
    >
      <div className={`w-8 h-8 rounded-lg ${iconBg} grid place-items-center`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-2.5 text-[9px] uppercase tracking-[0.2em] text-[#737373] font-bold">{label}</div>
      <div className="font-display font-extrabold text-2xl tracking-tighter leading-none text-[#0A0A0A] tabular-nums mt-0.5">
        {value}
      </div>
    </Comp>
  );
}

function FilterChip({ active, onClick, testid, children, tone = "default", show = true }) {
  if (!show) return null;
  const toneCls = {
    red: active ? "bg-red-700 text-white border-red-700" : "bg-white text-red-700 border-red-200 hover:border-red-400",
    amber: active ? "bg-amber-700 text-white border-amber-700" : "bg-white text-amber-800 border-amber-200 hover:border-amber-400",
    muted: active ? "bg-[#4A4A4A] text-white border-[#4A4A4A]" : "bg-white text-[#4A4A4A] border-[#E8E4D9] hover:border-[#0A0A0A]",
    default: active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E8E4D9] hover:border-[#0A0A0A]",
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg border text-xs font-semibold transition-colors ${toneCls}`}
    >
      {children}
    </button>
  );
}

function SortHeader({ label, k, cur, dir, onClick, align }) {
  const isActive = cur === k;
  return (
    <button
      onClick={() => onClick(k)}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${
        isActive ? "text-[#0A0A0A]" : "text-[#737373] hover:text-[#0A0A0A]"
      } ${align === "right" ? "justify-end" : ""}`}
    >
      {label}
      {isActive && (dir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />)}
    </button>
  );
}

function ClientRow({ c, selected, switching, onSelect, onActivate, onCopy }) {
  const hasAlert = c.sdi_rejected > 0;
  const hasPending = c.pending > 0;
  const dormant = isDormant(c.last_activity_at);
  const dot = c.health === "red" ? "bg-red-500" : c.health === "amber" ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(c.company_id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(c.company_id); } }}
      data-testid={`client-row-${c.company_id}`}
      className={`grid grid-cols-1 md:grid-cols-[36px_1fr_130px_90px_80px_120px_110px_140px] gap-3 md:gap-3 px-4 py-3 items-center cursor-pointer transition-colors ${
        selected
          ? "bg-[#1D4ED8]/[0.05] border-l-2 border-[#1D4ED8]"
          : hasAlert
          ? "hover:bg-red-50/40"
          : "hover:bg-[#F9F7F1]"
      }`}
    >
      {/* Health dot + avatar */}
      <div className="flex items-center justify-center relative">
        <div className={`w-2 h-2 rounded-full ${dot}`} title={`Stato: ${c.health}`} />
      </div>

      {/* Cliente */}
      <div className="flex items-center gap-2.5 min-w-0">
        <Avatar name={c.denominazione} active={c.is_active} small />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm text-[#0A0A0A] truncate flex items-center gap-1.5">
            <span className="truncate">{c.denominazione}</span>
            {c.is_active && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[9px] font-bold uppercase tracking-wider shrink-0">
                <Sparkles className="w-2.5 h-2.5" /> Attivo
              </span>
            )}
          </div>
          <div className="text-[10px] text-[#737373] font-mono truncate mt-0.5">
            {c.partita_iva ? `P.IVA ${c.partita_iva}` : "P.IVA mancante"}
          </div>
        </div>
      </div>

      {/* Priorità badges */}
      <div className="flex flex-wrap gap-1 md:justify-start">
        {hasAlert && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[9px] font-bold uppercase tracking-wider">
            <XCircle className="w-2.5 h-2.5" /> {c.sdi_rejected} scart{c.sdi_rejected === 1 ? "o" : "i"}
          </span>
        )}
        {hasPending && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">
            <Clock className="w-2.5 h-2.5" /> {c.pending}
          </span>
        )}
        {!hasAlert && !hasPending && dormant && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#F5F2EA] text-[#737373] text-[9px] font-bold uppercase tracking-wider">
            Dormiente
          </span>
        )}
        {!hasAlert && !hasPending && !dormant && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
            <CheckCircle2 className="w-2.5 h-2.5" /> OK
          </span>
        )}
      </div>

      {/* Fatture */}
      <div className="text-sm font-semibold text-[#0A0A0A] text-right tabular-nums">{c.invoices}</div>

      {/* SDI inviate */}
      <div className={`text-sm font-semibold text-right tabular-nums ${c.sdi_sent > 0 ? "text-[#059669]" : "text-[#737373]"}`}>
        {c.sdi_sent}
      </div>

      {/* Volume */}
      <div className="text-sm font-mono text-[#0A0A0A] text-right tabular-nums">{formatEurShort(c.amount_eur)}</div>

      {/* Attività */}
      <div className={`text-[11px] text-right tabular-nums ${dormant ? "text-[#737373]" : "text-[#4A4A4A]"}`}>
        {c.last_activity_at ? relativeTime(c.last_activity_at) : "\u2014"}
      </div>

      {/* Azioni */}
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {c.inbound_email && (
          <button
            onClick={() => onCopy(c.inbound_email)}
            data-testid={`client-copy-inbound-${c.company_id}`}
            className="p-1.5 rounded-md text-[#4A4A4A] hover:bg-[#F5F2EA] hover:text-[#0A0A0A]"
            title={`Copia ${c.inbound_email}`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
        {c.is_active ? (
          <Link
            to="/app/invoices"
            data-testid={`client-open-invoices-${c.company_id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#1D4ED8]"
          >
            Fatture <ArrowUpRight className="w-3 h-3" />
          </Link>
        ) : (
          <button
            onClick={() => onActivate(c.company_id)}
            disabled={switching === c.company_id}
            data-testid={`client-activate-${c.company_id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[#0A0A0A] text-[#0A0A0A] text-xs font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-60"
          >
            {switching === c.company_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apri"}
          </button>
        )}
      </div>
    </div>
  );
}

function ClientSidebar({ c, switching, onActivate, onCopy, onClose }) {
  const hasAlert = c.sdi_rejected > 0;
  const hasPending = c.pending > 0;
  const dormant = isDormant(c.last_activity_at);
  const dot = c.health === "red" ? "bg-red-500" : c.health === "amber" ? "bg-amber-500" : "bg-emerald-500";
  const healthLabel = c.health === "red" ? "Da gestire" : c.health === "amber" ? "In lavorazione" : "Tutto ok";

  return (
    <aside
      data-testid={`client-sidebar-${c.company_id}`}
      className="rounded-xl border border-[#E8E4D9] bg-white p-5 lg:sticky lg:top-6 lg:self-start"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            <Avatar name={c.denominazione} active={c.is_active} />
            <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${dot}`} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg tracking-tight text-[#0A0A0A] leading-tight truncate">
              {c.denominazione}
            </div>
            <div className="mt-0.5 text-[10px] font-mono text-[#737373] truncate">
              {c.partita_iva ? `P.IVA ${c.partita_iva}` : "P.IVA mancante"}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          data-testid="sidebar-close"
          className="p-1 rounded-md text-[#737373] hover:bg-[#F5F2EA] hover:text-[#0A0A0A]"
          title="Chiudi dettagli"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Health status pill */}
      <div className={`mb-4 rounded-lg px-3 py-2 text-xs flex items-center gap-2 ${
        c.health === "red"
          ? "bg-red-50 text-red-800 border border-red-200"
          : c.health === "amber"
          ? "bg-amber-50 text-amber-900 border border-amber-200"
          : "bg-emerald-50 text-emerald-800 border border-emerald-200"
      }`}>
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <span className="font-semibold">{healthLabel}</span>
        {hasAlert && <span>· {c.sdi_rejected} fattur{c.sdi_rejected === 1 ? "a" : "e"} scartat{c.sdi_rejected === 1 ? "a" : "e"}</span>}
        {!hasAlert && hasPending && <span>· {c.pending} da rivedere</span>}
      </div>

      {/* Mini KPIs */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <SidebarStat label="Fatture" value={c.invoices} icon={FileText} />
        <SidebarStat label="Inviate" value={c.sdi_sent} icon={Send} tone={c.sdi_sent > 0 ? "emerald" : "muted"} />
        <SidebarStat label="Scartate" value={c.sdi_rejected} icon={XCircle} tone={c.sdi_rejected > 0 ? "red" : "muted"} />
        <SidebarStat label="Da rivedere" value={c.pending} icon={Clock} tone={c.pending > 0 ? "amber" : "muted"} />
      </div>

      {/* Volume */}
      <div className="mb-4 rounded-lg bg-[#0A0A0A] text-white p-3.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/60 font-bold">
          <TrendingUp className="w-3 h-3" /> Volume trattato
        </div>
        <div className="mt-1 font-display font-extrabold text-2xl tracking-tighter leading-none tabular-nums">
          {formatEur(c.amount_eur)}
        </div>
      </div>

      {/* Inbound email */}
      {c.inbound_email && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#737373] font-bold mb-1.5">Email inbound</div>
          <div className="flex items-center gap-1.5 px-2.5 py-2 bg-[#F5F2EA] rounded-md">
            <Mail className="w-3.5 h-3.5 text-[#737373] shrink-0" />
            <code className="text-[11px] font-mono text-[#0A0A0A] truncate flex-1" title={c.inbound_email}>
              {c.inbound_email}
            </code>
            <button
              onClick={() => onCopy(c.inbound_email)}
              data-testid={`sidebar-copy-inbound-${c.company_id}`}
              className="p-1 rounded hover:bg-white"
              title="Copia email inbound"
            >
              <Copy className="w-3.5 h-3.5 text-[#4A4A4A]" />
            </button>
          </div>
        </div>
      )}

      {/* Last activity */}
      <div className="mb-5 flex items-center gap-1.5 text-xs text-[#4A4A4A]">
        <Clock className="w-3.5 h-3.5 text-[#737373]" />
        <span>
          Ultima attività:{" "}
          <span className={`font-semibold ${dormant ? "text-[#737373]" : "text-[#0A0A0A]"}`}>
            {c.last_activity_at ? relativeTime(c.last_activity_at) : "nessuna"}
          </span>
          {dormant && <span className="ml-1 text-[#737373]">(dormiente)</span>}
        </span>
      </div>

      {/* CTA */}
      <div className="flex flex-col gap-2">
        {c.is_active ? (
          <Link
            to="/app/invoices"
            data-testid={`sidebar-open-invoices-${c.company_id}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors"
          >
            Apri le fatture <ArrowUpRight className="w-4 h-4" />
          </Link>
        ) : (
          <button
            onClick={() => onActivate(c.company_id)}
            disabled={switching === c.company_id}
            data-testid={`sidebar-activate-${c.company_id}`}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors disabled:opacity-60"
          >
            {switching === c.company_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {switching === c.company_id ? "Attivazione…" : "Attiva cliente"}
          </button>
        )}
        <Link
          to="/app/account/companies"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-[#E8E4D9] text-[#4A4A4A] text-xs font-semibold hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
        >
          Anagrafica cliente
        </Link>
      </div>
    </aside>
  );
}

function SidebarStat({ label, value, icon: Icon, tone = "default" }) {
  const toneCls = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    red: "bg-red-50 text-red-700 border-red-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    muted: "bg-[#F9F7F1] text-[#737373] border-[#E8E4D9]",
    default: "bg-[#F9F7F1] text-[#0A0A0A] border-[#E8E4D9]",
  }[tone];
  return (
    <div className={`rounded-lg border p-3 ${toneCls}`}>
      <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-bold opacity-80">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="mt-1 font-display font-extrabold text-xl tracking-tighter tabular-nums leading-none">
        {value}
      </div>
    </div>
  );
}

function Avatar({ name, active, small }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const size = small ? "w-8 h-8 text-[10px]" : "w-10 h-10 text-xs";
  return (
    <div className={`${size} rounded-lg grid place-items-center shrink-0 font-bold ${
      active ? "bg-[#1D4ED8] text-white" : "bg-[#F5F2EA] text-[#4A4A4A]"
    }`}>
      {initials}
    </div>
  );
}

function EmptyState({ search, filter, onReset }) {
  const filtered = !!search || filter !== "all";
  return (
    <div className="p-10 text-center">
      <div className="w-12 h-12 rounded-xl bg-[#F5F2EA] grid place-items-center mx-auto mb-3">
        <Building2 className="w-5 h-5 text-[#737373]" />
      </div>
      <div className="font-display font-bold text-lg text-[#0A0A0A]">
        {filtered ? "Nessun cliente trovato" : "Nessun cliente ancora"}
      </div>
      <p className="text-sm text-[#4A4A4A] mt-1">
        {filtered ? "Prova a modificare i filtri o la ricerca" : "Aggiungi il primo cliente per iniziare"}
      </p>
      {filtered ? (
        <button
          onClick={onReset}
          data-testid="empty-reset-btn"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#E8E4D9] text-sm font-semibold hover:border-[#0A0A0A]"
        >
          Azzera filtri
        </button>
      ) : (
        <Link
          to="/app/account/companies"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors"
        >
          <Plus className="w-4 h-4" /> Aggiungi cliente
        </Link>
      )}
    </div>
  );
}

/* ---------- Utils ---------- */

function formatEur(n) {
  if (n == null) return "\u20ac 0,00";
  return `\u20ac ${Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatEurShort(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `\u20ac ${(v / 1000).toFixed(1)}k`;
  return `\u20ac ${v.toFixed(0)}`;
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    if (!t) return "";
    const diffSec = Math.max(0, (Date.now() - t) / 1000);
    if (diffSec < 60) return "ora";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m fa`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h fa`;
    if (diffSec < 2592000) return `${Math.floor(diffSec / 86400)}g fa`;
    if (diffSec < 31536000) return `${Math.floor(diffSec / 2592000)}mesi fa`;
    return `${Math.floor(diffSec / 31536000)}a fa`;
  } catch {
    return "";
  }
}

function isDormant(iso) {
  if (!iso) return true;
  try {
    const t = new Date(iso).getTime();
    if (!t) return true;
    const diffDays = (Date.now() - t) / 86400000;
    return diffDays >= 30;
  } catch {
    return false;
  }
}
